import { fail, minStatus, pass, pct, warn, type SeoRule } from '../rule';
import { bodySections, faqStartIndex } from '../sections';
import { T } from '../thresholds';
import { normalizeFr, splitSentences, tokenizeWords } from '../tokenize-fr';
import { SeoRuleKey, SeoScoreGroup } from '../types';

const G = SeoScoreGroup.STRUCTURE;

const wc = (text: string): number => tokenizeWords(normalizeFr(text)).length;

export const STRUCTURE_RULES: SeoRule[] = [
  {
    key: SeoRuleKey.SINGLE_H1,
    group: G,
    run: (ctx): ReturnType<SeoRule['run']> => {
      const fromInput = ctx.input.title !== undefined ? 1 : 0;
      const total = fromInput + ctx.h1InBody.length;
      if (total === 1) return pass(1, '1');
      if (total === 0) return fail(0, '1');
      return fail(
        total,
        '1',
        ctx.h1InBody.map((h): string => h.text),
      );
    },
  },
  {
    key: SeoRuleKey.HEADING_HIERARCHY,
    group: G,
    run: (ctx): ReturnType<SeoRule['run']> => {
      let jumps = 0;
      const locations: string[] = [];
      let previous = 1;
      for (const h of ctx.headings) {
        const level = h.level ?? 2;
        if (level > previous + 1) {
          jumps++;
          locations.push(`H${level} « ${h.text} » après H${previous}`);
        }
        previous = level;
      }
      if (jumps === 0) return pass(0, '0 saut');
      if (jumps === 1) return warn(1, '0 saut', locations);
      return fail(jumps, '0 saut', locations);
    },
  },
  {
    key: SeoRuleKey.H2_RATIO,
    group: G,
    run: (ctx): ReturnType<SeoRule['run']> => {
      const h2 = ctx.headings.filter((h): boolean => h.level === 2).length;
      const words = ctx.wordCount;
      const threshold = `1 H2 pour ${T.H2_WORDS_PER.PASS[0]}–${T.H2_WORDS_PER.PASS[1]} mots`;
      if (h2 === 0) {
        return words > T.H2_REQUIRED_ABOVE_WORDS ? fail('0 H2', threshold) : pass('0 H2 (texte court)', threshold);
      }
      const per = words / h2;
      const value = `1 H2 pour ${Math.round(per)} mots (${h2} H2)`;
      if (per >= T.H2_WORDS_PER.PASS[0] && per <= T.H2_WORDS_PER.PASS[1]) return pass(value, threshold);
      if (per <= T.H2_WORDS_PER.WARN_MAX && per >= T.H2_WORDS_PER.PASS[0] / 2) return warn(value, threshold);
      return fail(value, threshold);
    },
  },
  {
    key: SeoRuleKey.HEADING_LENGTH,
    group: G,
    run: (ctx): ReturnType<SeoRule['run']> => {
      if (ctx.headings.length === 0) return null;
      const long = ctx.headings.filter((h): boolean => h.text.length > T.HEADING_MAX_CHARS);
      const locations = long.map((h): string => h.text);
      if (long.length === 0) return pass(0, `≤ ${T.HEADING_MAX_CHARS} caractères`);
      if (long.length === 1) return warn(1, `≤ ${T.HEADING_MAX_CHARS} caractères`, locations);
      return fail(long.length, `≤ ${T.HEADING_MAX_CHARS} caractères`, locations);
    },
  },
  {
    key: SeoRuleKey.EMPTY_SECTION,
    group: G,
    run: (ctx): ReturnType<SeoRule['run']> => {
      if (ctx.headings.length === 0) return null;
      const blocks = ctx.textBlocks;
      let empty = 0;
      const locations: string[] = [];
      for (let i = 0; i < blocks.length; i++) {
        const b = blocks[i];
        if (b.type !== 'heading') continue;
        const next = blocks[i + 1];
        if (!next || next.type === 'heading') {
          // Un H2 suivi directement d'un H3 est un plan acceptable ; H2 → H2 ou titre final = vide.
          if (next && (next.level ?? 0) > (b.level ?? 0)) continue;
          empty++;
          locations.push(b.text);
        }
      }
      if (empty === 0) return pass(0, '0');
      if (empty === 1) return warn(1, '0', locations);
      return fail(empty, '0', locations);
    },
  },
  {
    key: SeoRuleKey.INTRO_PRESENT,
    group: G,
    run: (ctx): ReturnType<SeoRule['run']> => {
      const firstHeadingIdx = ctx.textBlocks.findIndex((b): boolean => b.type === 'heading' && (b.level ?? 0) >= 2);
      const intro = (firstHeadingIdx === -1 ? ctx.textBlocks : ctx.textBlocks.slice(0, firstHeadingIdx)).filter(
        (b): boolean => b.type === 'paragraph',
      );
      const words = intro.reduce((sum, b): number => sum + wc(b.text), 0);
      if (words >= T.INTRO_MIN_WORDS) return pass(`${words} mots`, `≥ ${T.INTRO_MIN_WORDS} mots`);
      if (words > 0) return warn(`${words} mots`, `≥ ${T.INTRO_MIN_WORDS} mots`);
      // Un article qui ouvre sur un H2 n'est pas publiable tel quel : bloquant en strict.
      return fail('aucune', `≥ ${T.INTRO_MIN_WORDS} mots`, undefined, true);
    },
  },
  {
    key: SeoRuleKey.PARAGRAPH_LENGTH,
    group: G,
    run: (ctx): ReturnType<SeoRule['run']> => {
      const paragraphs = ctx.bodyBlocks.filter((b): boolean => b.type === 'paragraph');
      if (paragraphs.length === 0) return null;
      const long = paragraphs.filter((p): boolean => wc(p.text) > T.PARAGRAPH_MAX_WORDS);
      const ratio = 1 - long.length / paragraphs.length;
      return minStatus(
        ratio,
        T.PARAGRAPH_OK_RATIO.PASS,
        T.PARAGRAPH_OK_RATIO.WARN,
        (v): string => `${pct(v)} ≤ ${T.PARAGRAPH_MAX_WORDS} mots`,
        long.map((p): string => `${p.text.slice(0, 60)}… (${wc(p.text)} mots)`),
      );
    },
  },
  {
    key: SeoRuleKey.PARAGRAPH_VARIETY,
    group: G,
    run: (ctx): ReturnType<SeoRule['run']> => {
      const paragraphs = ctx.bodyBlocks.filter((b): boolean => b.type === 'paragraph');
      if (paragraphs.length < T.SINGLE_SENTENCE_RUN) return null;
      let run = 0;
      let runs = 0;
      for (const p of paragraphs) {
        if (splitSentences(p.text).length <= 1) {
          run++;
          if (run === T.SINGLE_SENTENCE_RUN) runs++;
        } else run = 0;
      }
      if (runs === 0) return pass(0, '0 suite');
      if (runs === 1) return warn(1, '0 suite');
      return fail(runs, '0 suite');
    },
  },
  {
    key: SeoRuleKey.PARAGRAPH_UNIFORMITY,
    group: G,
    run: (ctx): ReturnType<SeoRule['run']> => {
      // Hors FAQ : ses réponses, courtes par construction, gonfleraient la variance.
      const faqStart = faqStartIndex(ctx);
      const paragraphs = ctx.bodyBlocks.filter((b): boolean => b.type === 'paragraph' && b.index < faqStart);
      if (paragraphs.length < 6) return null;
      const lengths = paragraphs.map((p): number => wc(p.text));
      const mean = lengths.reduce((a, b): number => a + b, 0) / lengths.length;
      if (mean === 0) return null;
      const variance = lengths.reduce((a, l): number => a + (l - mean) ** 2, 0) / lengths.length;
      const cv = Math.sqrt(variance) / mean;
      const sections = bodySections(ctx);
      const counts = sections.map((s): number => s.blocks.filter((b): boolean => b.type === 'paragraph').length);
      const uniformShape = counts.length >= T.UNIFORM_SECTIONS_MIN && new Set(counts).size === 1;
      const cvLabel = `variation ${Math.round(cv * 100) / 100}`;
      const value = uniformShape ? `${cvLabel}, ${counts.length} sections × ${counts[0]} paragraphes` : cvLabel;
      const threshold = `variation ≥ ${T.PARAGRAPH_CV.PASS}, sections de formes différentes`;
      if (cv < T.PARAGRAPH_CV.WARN) return fail(value, threshold);
      if (cv < T.PARAGRAPH_CV.PASS || uniformShape) return warn(value, threshold);
      return pass(value, threshold);
    },
  },
  {
    key: SeoRuleKey.LIST_OR_TABLE_SANITY,
    group: G,
    run: (ctx): ReturnType<SeoRule['run']> => {
      const listWords = ctx.doc.blocks
        .filter((b): boolean => b.type === 'list_item')
        .reduce((sum, b): number => sum + wc(b.text), 0);
      const hasLists = ctx.doc.listSizes.length > 0;
      const hasTables = ctx.doc.blocks.some((b): boolean => b.type === 'table_cell');
      if (!hasLists && !hasTables) return null;
      const anomalies: string[] = [];
      if (ctx.wordCount > 0 && listWords / ctx.wordCount > T.LIST_MAX_TEXT_RATIO) {
        anomalies.push(`listes = ${pct(listWords / ctx.wordCount)} du texte`);
      }
      const singletons = ctx.doc.listSizes.filter((s): boolean => s === 1).length;
      if (singletons > 0) anomalies.push(`${singletons} liste(s) à un seul élément`);
      if (ctx.doc.tablesWithoutHeader > 0) anomalies.push(`${ctx.doc.tablesWithoutHeader} tableau(x) sans en-tête`);
      if (anomalies.length === 0) return pass(0, '0 anomalie');
      if (anomalies.length === 1) return warn(1, '0 anomalie', anomalies);
      return fail(anomalies.length, '0 anomalie', anomalies);
    },
  },
  {
    key: SeoRuleKey.FAQ_PRESENT,
    group: G,
    run: (ctx): ReturnType<SeoRule['run']> => {
      const faqHeading = ctx.headings.some((h): boolean => /\b(faq|questions?)\b/.test(normalizeFr(h.text)));
      const questionHeadings = ctx.headings.filter((h): boolean => h.text.trim().endsWith('?')).length;
      if (faqHeading || questionHeadings >= T.FAQ_MIN_QUESTION_HEADINGS) return pass('présente');
      return fail('absente', 'bonus');
    },
  },
];
